import { create } from 'zustand'

/**
 * Network + offline-queue state.
 *
 * One Zustand store, three concerns:
 *
 *   - **Network**: `isOnline` mirrors `navigator.onLine`. The
 *     sync controller (`lib/offline/sync-controller.ts`) updates
 *     it from `online` / `offline` events. `wasOffline` flips
 *     true the first time we drop offline so the UI can announce
 *     "Reconnecté" the next time we come back.
 *
 *   - **Queue counts**: `pendingCount` and `errorCount` come from
 *     IndexedDB. We mirror them in the store so a re-render
 *     happens immediately when the queue changes — TanStack
 *     Query alone isn't a good fit for a non-HTTP source like
 *     IDB.
 *
 *   - **Sync activity**: `syncing` is true while
 *     `syncPendingMissions` is walking the queue. The header
 *     uses it to swap the "hors connexion" banner for a
 *     "synchronisation en cours" one.
 *
 * Selectors **must** use primitive picks or `useShallow` (the
 * boucle-infinie lesson from the previous fix). The hook in
 * `use-sync-status.ts` does this for you.
 */
export interface SyncState {
  isOnline: boolean
  wasOffline: boolean
  syncing: boolean
  pendingCount: number
  errorCount: number

  setOnline: (online: boolean) => void
  setSyncing: (syncing: boolean) => void
  setCounts: (counts: { pending: number; error: number }) => void
}

export const useSyncStore = create<SyncState>((set) => ({
  // Default to true on the server / first render — flipping to
  // false there would briefly show the offline banner during
  // hydration.
  isOnline:
    typeof navigator === 'undefined' ? true : navigator.onLine,
  wasOffline: false,
  syncing: false,
  pendingCount: 0,
  errorCount: 0,

  setOnline(online) {
    set((s) => ({
      isOnline: online,
      wasOffline: s.wasOffline || !online,
    }))
  },
  setSyncing(syncing) {
    set({ syncing })
  },
  setCounts({ pending, error }) {
    set({ pendingCount: pending, errorCount: error })
  },
}))
